import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  UpdateDeliveredCouponFieldsDto,
  updateDeliveredCouponFields,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponSearchHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponRedeemStatus,
  GiftCouponSearchParams,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useQueryClient } from 'react-query';

import { executeUndeliver, formatUndeliverToast } from '../utils/undeliver.utils';
import { useSessionPersistedState } from './useSessionPersistedState';
import { useToast } from './useToast';

/** KST 기준 오늘 날짜 (YYYY-MM-DD) */
export const todayKSTString = () =>
  new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 발송내역 조회 페이지 훅.
 *
 * 별도 백엔드 API 없이 기존 /admin/gift-coupon/search 를 사용한다 —
 * redeemStatus=Delivered + gifticon.deliveredAt KST 하루 범위(dateField='deliveredAt')로
 * 조회하면 발송대기 대시보드의 todayDelivered와 동일한 populate/shape이 내려온다.
 * 도매가/매체 일괄수정도 날짜와 무관한 generic PATCH(updateDeliveredCouponFields)를 재사용.
 */
export function useDeliveredHistoryPage() {
  const queryClient = useQueryClient();

  // 새로고침 후에도 보던 날짜 유지 (환불/수정 후 reload 대비)
  const [date, setDate] = useSessionPersistedState(
    'giftDeliveredHistory.date',
    todayKSTString(),
  );

  // 잔액 변경내역 모달 등에서 ?date=YYYY-MM-DD로 진입하면 그 날짜를 연다 (진입 시 1회)
  const appliedUrlDateRef = useRef(false);
  useEffect(() => {
    if (appliedUrlDateRef.current) return;
    appliedUrlDateRef.current = true;
    const param = new URLSearchParams(window.location.search).get('date');
    if (param && DATE_PATTERN.test(param)) setDate(param);
  }, [setDate]);
  const isValidDate = DATE_PATTERN.test(date);
  const isToday = date === todayKSTString();

  const searchParams = useMemo<GiftCouponSearchParams>(
    () => ({
      redeemStatus: [GiftCouponRedeemStatus.Delivered],
      dateFrom: date,
      dateTo: date,
      dateField: 'deliveredAt',
    }),
    [date],
  );
  const searchKey = `deliveredHistory:${date}`;

  useGiftCouponSearchHook(searchKey, searchParams, !isValidDate);

  const status = useRecoilValue(giftCouponSearchStatusAtomFamily(searchKey));
  const rawResults = useRecoilValue(giftCouponSearchDataAtomFamily(searchKey));

  // 발송시각 내림차순 → 같은 묶음 내 신청시각 오름차순 (발송대기 페이지의 오늘발송완료와 동일 정렬)
  const delivered = useMemo(() => {
    const list = rawResults ?? [];
    return [...list].sort((a, b) => {
      const deliveredA = a.gifticon?.deliveredAt
        ? new Date(a.gifticon.deliveredAt).getTime()
        : 0;
      const deliveredB = b.gifticon?.deliveredAt
        ? new Date(b.gifticon.deliveredAt).getTime()
        : 0;
      if (deliveredA !== deliveredB) return deliveredB - deliveredA;
      const requestedA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const requestedB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return requestedA - requestedB;
    });
  }, [rawResults]);

  // 날짜 이동 (UTC 자정 기준 문자열 연산이라 타임존 영향 없음)
  const shiftDate = useCallback(
    (days: number) => {
      if (!isValidDate) return;
      const d = new Date(`${date}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + days);
      setDate(d.toISOString().slice(0, 10));
    },
    [date, isValidDate, setDate],
  );

  const goToday = useCallback(() => setDate(todayKSTString()), [setDate]);

  const [showTable, setShowTable] = useState(true);

  const {
    message: toastMessage,
    show: showToast,
    durationMs: toastDurationMs,
  } = useToast(1200);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 도매가/매체 일괄 수정 — 발송대기 페이지의 handleBulkUpdateDeliveredCoupon과 동일 로직
  const handleBulkUpdateDelivered = useCallback(
    async (couponIds: string[], dto: UpdateDeliveredCouponFieldsDto) => {
      if (couponIds.length === 0) return;
      if (dto.deliveryChannel === undefined && dto.priceWhole === undefined) return;

      const parts: string[] = [];
      if (dto.priceWhole !== undefined) {
        parts.push(`도매가: ${dto.priceWhole.toLocaleString()}원`);
      }
      if (dto.deliveryChannel !== undefined) {
        parts.push(`매체: ${dto.deliveryChannel || '입력안함'}`);
      }
      if (
        !confirm(`${couponIds.length}건을 다음과 같이 수정합니다.\n${parts.join('\n')}`)
      ) {
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const id of couponIds) {
        try {
          await updateDeliveredCouponFields(id, dto);
          successCount++;
        } catch (error) {
          console.error(`쿠폰 수정 실패 (${id}):`, error);
          failCount++;
        }
      }

      // 이 페이지의 검색 결과 + 발송대기 대시보드(오늘 날짜 수정 시) 모두 갱신
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });

      if (failCount === 0) {
        showToast(`${successCount}건 수정 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    },
    [queryClient, showToast],
  );

  // 발송완료 취소 — 발송대기로 복귀 + 원장 자동 환급 (스펙: docs/gift-coupon-undeliver-spec.md)
  const handleBulkUndeliver = useCallback(
    async (couponIds: string[], reason: string) => {
      const result = await executeUndeliver(couponIds, reason);
      if (!result) return false;
      // 이 페이지 검색 결과 + 발송대기 대시보드(Requested 복귀분) 모두 갱신
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
      showToast(formatUndeliverToast(result));
      return true;
    },
    [queryClient, showToast],
  );

  return {
    // 날짜
    date,
    setDate,
    isToday,
    isValidDate,
    shiftDate,
    goToday,

    // 데이터
    status,
    delivered,

    // 테이블 접기/펼치기
    showTable,
    setShowTable,

    // 일괄 수정 / 발송취소
    handleBulkUpdateDelivered,
    handleBulkUndeliver,

    // 토스트/클립보드
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,
  };
}
